import { useState } from "react";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export function ConsultForm({ onDone, compact = false }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", goal: "general", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/consults", { ...form, language: lang });
      setDone(true);
      toast.success(t("contact.success"));
      onDone?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="text-center py-8" data-testid="consult-success">
        <CheckCircle2 className="w-12 h-12 text-secondary mx-auto mb-3" />
        <p className="font-serif text-xl font-semibold">{t("contact.success")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="consult-form">
      <div className="space-y-1.5">
        <Label htmlFor="consult-name">{t("contact.name")}</Label>
        <Input id="consult-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="consult-name-input" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="consult-email">{t("contact.email")}</Label>
        <Input id="consult-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="consult-email-input" />
      </div>
      <div className="space-y-1.5">
        <Label>{t("contact.goal")}</Label>
        <Select value={form.goal} onValueChange={(v) => setForm({ ...form, goal: v })}>
          <SelectTrigger data-testid="consult-goal-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["citizenship", "residency", "job", "exam", "business", "general"].map((g) => (
              <SelectItem key={g} value={g} data-testid={`consult-goal-${g}`}>{t(`goals.${g}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="consult-message">{t("contact.message")}</Label>
        <Textarea id="consult-message" rows={compact ? 2 : 4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="consult-message-input" />
      </div>
      <Button type="submit" className="w-full" size="lg" disabled={busy} data-testid="consult-submit-btn">
        {busy ? t("contact.sending") : t("contact.send")}
      </Button>
    </form>
  );
}

export default function ConsultModal({ open, onOpenChange }) {
  const { t } = useLang();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="consult-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("contact.heading")}</DialogTitle>
          <DialogDescription>{t("contact.sub")}</DialogDescription>
        </DialogHeader>
        <ConsultForm compact onDone={() => setTimeout(() => onOpenChange(false), 1800)} />
      </DialogContent>
    </Dialog>
  );
}
